define(["require", "exports", "knockout", "./deterministic-stringify"], function (require, exports, ko, deterministic_stringify_1) {
    "use strict";
    Object.defineProperty(exports, "__esModule", { value: true });
    exports.ignoreArray = exports.HandleOrProxy = void 0;
    function HandleOrProxy(handler) {
        return (handlerOrData, e, ...args) => {
            if (typeof handlerOrData == "function") {
                const wrappedHandler = (data, e, ...args) => {
                    handler(data, e, ...args);
                    return !!handlerOrData(data, e, ...args);
                };
                return wrappedHandler;
            }
            else {
                return handler(handlerOrData, e, ...args);
            }
        };
    }
    exports.HandleOrProxy = HandleOrProxy;
    class KnockoutUtils {
        constructor() { }
        static isObservableArray(instance) {
            return ko.isObservable(instance) &&
                typeof instance["remove"] == "function" &&
                typeof instance["removeAll"] == "function" &&
                typeof instance["destroy"] == "function" &&
                typeof instance["destroyAll"] == "function" &&
                typeof instance["indexOf"] == "function" &&
                typeof instance["replace"] == "function";
        }
        static asObservable(obsArray) {
            let proxyObs = ko.observable(null);
            let internalModify = false;
            this.onObservableHashChanged(obsArray, newValues => {
                if (internalModify) {
                    return;
                }
                internalModify = true;
                if (!newValues || newValues.length === 0) {
                    proxyObs(null);
                }
                else {
                    proxyObs(newValues[0]);
                }
                internalModify = false;
            }, 25, true);
            proxyObs.subscribe(newValue => {
                if (internalModify) {
                    return;
                }
                internalModify = true;
                const oldValues = obsArray.peek();
                if (oldValues.length > 1) {
                    oldValues.splice(1, oldValues.length - 1);
                }
                if (KnockoutUtils.isObservableArray(obsArray)) {
                    if (newValue != null) {
                        if (oldValues.length < 1) {
                            obsArray.push(newValue);
                        }
                        else if (obsArray.indexOf(newValue) < 0) {
                            obsArray.replace(oldValues[0], newValue);
                        }
                    }
                    else if (oldValues.length > 0) {
                        obsArray.removeAll();
                    }
                }
                else {
                    if (newValue != null) {
                        obsArray([newValue]);
                    }
                    else {
                        obsArray([]);
                    }
                }
                internalModify = false;
            });
            return proxyObs;
        }
        static asObservableArray(obs, canEmpty = false) {
            if (typeof canEmpty !== "function") {
                const canEmptyBool = !!canEmpty;
                canEmpty = () => canEmptyBool;
            }
            const initialValue = obs.peek();
            const obsArray = ko.observableArray(initialValue ? [initialValue] : []);
            let internalModify = false;
            obs.subscribe(newValue => {
                if (internalModify) {
                    return;
                }
                internalModify = true;
                const oldValues = obsArray.peek();
                if (oldValues.length > 1) {
                    oldValues.splice(1, oldValues.length - 1);
                }
                if (newValue != null) {
                    if (oldValues.length < 1) {
                        obsArray.push(newValue);
                    }
                    else if (obsArray.indexOf(newValue) < 0) {
                        obsArray.replace(oldValues[0], newValue);
                    }
                }
                else if (oldValues.length > 0) {
                    obsArray.removeAll();
                }
                internalModify = false;
            });
            let oldArrValues = [];
            obsArray.subscribe(oldValues => {
                oldArrValues = oldValues.slice();
            }, this, "beforeChange");
            obsArray.subscribe(newValues => {
                if (internalModify) {
                    return;
                }
                internalModify = true;
                if (newValues.length > 1) {
                    newValues.splice(0, 1, newValues[newValues.length - 1]);
                    newValues.splice(1, newValues.length - 1);
                }
                if (!newValues.length && oldArrValues.length && !canEmpty(oldArrValues[oldArrValues.length - 1])) {
                    newValues.push(oldArrValues[oldArrValues.length - 1]);
                }
                if (newValues.length) {
                    obs(newValues[0]);
                }
                else {
                    obs(null);
                }
                internalModify = false;
            });
            return obsArray;
        }
        static stringifyObservable(obs) {
            const value = obs();
            let target = value;
            if (ko.mapping.isMapped(value)) {
                target = ko.mapping.toJS(value);
            }
            else if (Array.isArray(value) && value.length > 0 && ko.mapping.isMapped(value[0])) {
                target = value.map(item => ko.mapping.toJS(item));
            }
            return (0, deterministic_stringify_1.deterministicStringify)(target);
        }
        static onObservableHashChanged(obs, onChanged, timeout, fireImmediate = true) {
            let computed = ko.pureComputed(() => KnockoutUtils.stringifyObservable(obs));
            if (!!timeout)
                computed = computed.extend({ rateLimit: { method: "notifyWhenChangesStop", timeout: timeout } });
            const subscription = computed.subscribe(() => onChanged(obs.peek()));
            if (fireImmediate)
                onChanged(obs.peek());
            return subscription;
        }
        static linkedObservable(valueAccessor) {
            let internalObs;
            let valueSubscription;
            const destroyInternalObservable = () => {
                internalObs = null;
                if (valueSubscription)
                    valueSubscription.dispose();
            };
            const createInternalObservable = (value) => {
                internalObs = ko.observable(value);
                valueSubscription = ko.pureComputed(() => ko.unwrap(valueAccessor()))
                    .subscribe(internalObs);
            };
            const getObservable = () => {
                const value = valueAccessor();
                if (ko.isWriteableObservable(value)) {
                    destroyInternalObservable();
                    return value;
                }
                if (internalObs == null) {
                    createInternalObservable(value);
                }
                return internalObs;
            };
            return ko.pureComputed({
                read: () => getObservable()(),
                write: value => getObservable()(value)
            });
        }
        static whenForever(predicate, callback, context) {
            function kowhen(resolve) {
                var observable = ko.pureComputed(predicate, context).extend({ notify: 'always' });
                var subscription = observable.subscribe(function (value) {
                    if (value) {
                        resolve(value);
                    }
                });
                // In case the initial value is true, process it right away
                observable['notifySubscribers'](observable.peek());
                return subscription;
            }
            if (typeof Promise === "function" && !callback) {
                return new Promise(kowhen);
            }
            else {
                return kowhen(callback.bind(context));
            }
        }
        ;
        static rateLimit(target, options) {
            ko.extenders.rateLimit(target, options);
            return target;
        }
        static createSubscription(subscribable, callback, target = this, event = "change", afterDisposalExecutionCount = 0) {
            let isDisposed = false;
            let disposedExecutionCount = 0;
            const subscription = subscribable.subscribe(x => {
                callback(x);
                if (isDisposed) {
                    disposedExecutionCount++;
                    if (disposedExecutionCount >= afterDisposalExecutionCount) {
                        subscription.dispose();
                    }
                }
            }, target, event);
            return {
                dispose: () => {
                    if (afterDisposalExecutionCount === 0) {
                        subscription.dispose();
                    }
                    else {
                        isDisposed = true;
                    }
                }
            };
        }
    }
    exports.default = KnockoutUtils;
    /**
    * Helper function that returns another function which takes `string[]` rest parameter(s) and gives back these same parameters as an array.
    *
    * Typing ensures that the resulting array implements `T`, which is useful when building a `ko.mapping` "ignore" array
    * which needs to be a comprehensive list of property names for a given type (`T`)
    *
    * @example
    * class Rectangle {
    *     len: KnockoutObservable<number>, width: KnockoutObservable<number>;
    *     area: KnockoutComputed<number>, description: KnockoutComputed<string>;
    *
    *     constructor(l: number, w: number) {
    *         this.len = ko.observable(l);
    *         this.width = ko.observable(w);
    *         this.area = ko.computed(() => this.len() * this.width());
    *         this.description = ko.computed(() => "Length: " + this.len() + ", Width: " + this.width())
    *     }
    * }
    * const sourceObj = new Rectangle(10, 20);
    *
    * type computedRectangleProps = keyof({
    *     [p in keyof Rectangle as Rectangle[p] extends KnockoutComputed<any> ? p : never]: Rectangle[p]
    * });
    *
    * // NOTE: TypeScript gives an error if we do not include both 'area' AND 'description' in the params
    * const mappingIgnore = ignoreArray<computedRectangleProps>()('area', 'description')
    *
    * const destinationObj = ko.mapping.fromJS(sourceObj, { ignore: mappingIgnore });
    */
    const ignoreArray = () => (...x) => x;
    exports.ignoreArray = ignoreArray;
});
//# sourceMappingURL=knockout-utils.js.map